import { useLocalStorage, useTimeoutPoll } from "@vueuse/core";
import { computed, inject, provide, toRef, type InjectionKey, type Ref } from "vue";
import { Translation } from "vue-i18n";
import type { Download } from "@/entities/Entities";
import { useNotify } from "@/components/utils/notifications";
import { fetchApi, fetchApiBase } from "./fetch";
import { v4 as uuidv4 } from "uuid";
import TasksVue from "@/Tasks.vue";

interface TaskStorage<TData, TResult> {
  id: string;
  done: boolean;
  data: TData;
  error?: boolean;
  result?: TResult;
}

type Task<TData, TOutput> = (item: TData) => Promise<{ done: boolean; result?: TOutput }>;
export function useLongPolling<TData, TOutput>(
  tasks: Ref<TaskStorage<TData, TOutput>[]>,
  task: Task<TData, TOutput>,
  doneCb: (task: TaskStorage<TData, TOutput>) => void,
  errorCb: (task: TaskStorage<TData, TOutput>) => void,
  timeoutMs: number
) {
  const pendingTasks = computed(() => tasks.value.filter((task) => !task.done && !task.error));
  const doneTasks = computed(() => tasks.value.filter((task) => task.done));
  const errorTasks = computed(() => tasks.value.filter((task) => task.error));
  doneTasks.value.forEach(doneCb);
  errorTasks.value.forEach(errorCb);
  const controls = useTimeoutPoll(
    async () => {
      await Promise.allSettled(
        pendingTasks.value.map(async (taskData) => {
          if (!taskData.done) {
            try {
              const { result, done } = await task(taskData.data);
              taskData.result = result;
              taskData.done = done;
              if (done) {
                doneCb(taskData);
              }
            } catch (e) {
              taskData.error = true;
              errorCb(taskData);
            }
          } else {
            doneCb(taskData);
          }
        })
      );
    },
    timeoutMs,
    { immediate: !!tasks.value.length }
  );

  return controls;
}

export function useTaskStorage<TData, TOutput>(
  key: string,
  poll: Task<TData, TOutput>,
  doneCb: (item: TaskStorage<TData, TOutput>) => void,
  errorCb: (item: TaskStorage<TData, TOutput>) => void,
  timeoutMs = 2000
) {
  const tasks = useLocalStorage(key, [] as TaskStorage<TData, TOutput>[]);
  const addTask = (taskData: TData) => {
    tasks.value = [...tasks.value, { done: false, data: taskData, id: uuidv4() }];
    // eslint-disable-next-line no-use-before-define
    if (!isActive.value) {
      // eslint-disable-next-line no-use-before-define
      resume();
    }
  };
  const removeTask = (taskToRemove: TaskStorage<TData, TOutput>) => {
    tasks.value = tasks.value.filter((task) => task.id !== taskToRemove.id);
    if (isActive.value && !tasks.value.length) {
      pause();
    }
  };
  const { isActive, resume, pause } = useLongPolling(tasks, poll, doneCb, errorCb, timeoutMs);
  return {
    tasks,
    addTask,
    removeTask,
  };
}

export interface Tasks<TData> {
  tasks: Ref<TData[]>;
  addTask: (task: TData) => void;
}

export function createTaskProvider<TData>(key: string) {
  const fullKey = `jk/tasks-${key}`;
  const tasksKey = Symbol(fullKey) as InjectionKey<Tasks<TData>>;
  return {
    key: tasksKey,
    inject: () => inject(tasksKey),
    provide: (data: Tasks<TData>) => provide(tasksKey, data),
  };
}

export const { inject: useDownloads, provide: provideDownloads } =
  createTaskProvider<Download>("downloads");
export function useDownloadTasks() {
  const notify = useNotify();
  const controls = useTaskStorage<Download, Download>(
    "downloads",
    async (task) => {
      const result = await fetchApi<Download>(task["@id"]);
      return {
        result,
        done: !!result?.file,
      };
    },
    async (task) => {
      if (!task.result) return;
      const response = await fetchApiBase(task.result.file?.contentUrl ?? '');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const downloaded = () => {
        controls.removeTask(task);
        dismiss?.();
      };
      const { dismiss } =
        notify?.({
          variant: "download",
          group: "download",
          duration: -1,
          onClose: () => {
            controls.removeTask(task);
          },
          title: (
            <Translation keypath="file.notifySuccess">
              <a href={url} onClick={downloaded} download={task.result.file?.originalFileName}>
                {task.result.file?.originalFileName}
              </a>
            </Translation>
          ),
        }) ?? {};
    },
    async (task) => {
      controls.removeTask(task);
    }
  );

  return controls;
}
